import React, {
  FC,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  CheckboxProps,
  Form,
  HtmlLabelProps,
  SemanticShorthandItem,
} from 'semantic-ui-react';
import SearchPanel from '../SearchPanel';
import './POSMultiselectDropdown.less';
import POSCheckboxList from '../POSCheckBoxList';
import { POSMultiselectTags } from './POSMultiselectTags';
import POSIcon from '../POSIcon';
import csx from 'classnames';

export interface POSMultiselectOption {
  label: string;
  value: number | string;
  checked: boolean;
}

export type POSMultiselectDropdownProps = {
  label?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  options: Array<POSMultiselectOption>;
  dataTestId?: string;
  onSelect: (value: SemanticShorthandItem<HtmlLabelProps>) => void;
  required?: boolean;
  error?: string;
};

export const POSMultiselectDropdown: FC<POSMultiselectDropdownProps> = ({
  label,
  placeholder,
  searchPlaceholder,
  options,
  dataTestId,
  onSelect,
  required,
  error,
}) => {
  const itemsSectionRef = useRef<any>();
  const [open, setOpen] = useState<boolean>(false);
  const [searchString, setSearchString] = useState<string>('');

  const selectHandler = useCallback(
    (_e: SyntheticEvent<HTMLElement, Event>, data: CheckboxProps) => {
      onSelect && onSelect(data.value);
    },
    [onSelect]
  );

  useEffect(() => {
    const handleDocumentMouseUp = (event: any) => {
      if (!itemsSectionRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mouseup', handleDocumentMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleDocumentMouseUp);
    };
  }, []);

  const filteredOptions = useMemo(
    () =>
      searchString
        ? options.filter((el: POSMultiselectOption) =>
            el.label
              .toLocaleLowerCase()
              .includes(searchString.toLocaleLowerCase())
          )
        : options,
    [options, searchString]
  );

  const labelsSelected = useMemo(() => {
    return options.filter((i) => i.checked).map((i) => i.label);
  }, [options]);

  const placeholderString = useMemo(() => {
    return options.length ? placeholder : 'No option available ...';
  }, [options, placeholder]);

  const toggleOpen = useCallback(() => {
    setOpen((currentToggleState) => !currentToggleState);
  }, []);

  return (
    <Form.Field
      className={csx('p-os-multiselect-wrapper', {
        required,
        error: !!error,
      })}
      data-testid={!!dataTestId ? dataTestId : 'p-os-multiselect-wrapper'}
    >
      <label className="form-label">{label}</label>
      <div ref={itemsSectionRef}>
        <div
          onClick={toggleOpen}
          className={csx('tags-section', {
            disabled: !options.length,
            focused: open,
          })}
        >
          <POSMultiselectTags
            selectedOptions={labelsSelected}
            placeholder={placeholderString}
          />
          {options.length ? (
            <POSIcon icon={open ? 'arrow-up' : 'arrow-down'} />
          ) : null}
        </div>

        {options.length
          ? open && (
              <div className="items-section">
                <SearchPanel
                  submitAlways={true}
                  searchString={searchString}
                  onSubmit={setSearchString}
                  loading={false}
                  placeHolder={searchPlaceholder || 'Type to search...'}
                />
                <div className="items-checkboxes">
                  <POSCheckboxList
                    items={filteredOptions}
                    onChange={selectHandler}
                  />
                </div>
              </div>
            )
          : null}
      </div>
      {required && !!error && <label className="error">{error}</label>}
    </Form.Field>
  );
};
